import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { isValidJwt } from "../../apiCalls/apiHandler";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider(props) {
    const getInitialState = () => {
        try {
            const raw = localStorage.getItem('99GIFT');
            if (!raw) return { authUser: null, isLoggedIn: false };

            const parsed = JSON.parse(raw);
            const token = parsed?.token;
            if (!token || !isValidJwt(token)) {
                localStorage.removeItem('99GIFT');
                return { authUser: null, isLoggedIn: false };
            }

            const decoded = jwtDecode(token);
            // If passwordExists is explicitly false and the user hasn't completed setting a password,
            // they are in an intermediate onboarding state (not yet fully authenticated)
            if (decoded?.passwordExists === false && !parsed?.passwordCompleted) {
                return { authUser: parsed, isLoggedIn: false };
            }

            return { authUser: parsed, isLoggedIn: true };
        } catch (e) {
            return { authUser: null, isLoggedIn: false };
        }
    };

    const [{ authUser, isLoggedIn }, setAuthState] = useState(getInitialState);
    const [isLoading, setIsLoading] = useState(false);

    const setAuthUser = useCallback((user) => {
        setAuthState(prev => ({ ...prev, authUser: user }));
    }, []);

    const setIsLoggedIn = useCallback((loggedIn) => {
        setAuthState(prev => {
            let updatedUser = prev.authUser;
            if (!loggedIn) {
                updatedUser = null;
            } else if (!updatedUser) {
                try {
                    const raw = localStorage.getItem('99GIFT');
                    if (raw) updatedUser = JSON.parse(raw);
                } catch (_) {}
            }
            return {
                authUser: updatedUser,
                isLoggedIn: loggedIn
            };
        });
    }, []);

    // Listen to localStorage changes across tabs/windows or manual triggers
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === '99GIFT') {
                const updated = getInitialState();
                setAuthState(updated);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const value = {
        authUser,
        setAuthUser,
        isLoggedIn,
        setIsLoggedIn,
        isLoading,
        setIsLoading
    };

    return (
        <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
    );
}